/**
 * Per-node FTE — one formula for the List/Finance tables and both charts (WQ-343).
 *
 * node FTE = (iRnD + fRnD) / (avgFteCost × fteEfficiency), rounded to 2 dp.
 * Swim-lane investment is not attributed to nodes; it stays in the totals and
 * gap cards only.
 */

import type { AdjacencyNode } from '../types/adjacency';
import type { ResourceConfig } from '../types/resource-config';

type FteConfig = Pick<ResourceConfig, 'avgFteCost' | 'fteEfficiency'>;

const round2 = (v: number) => Math.round(v * 100) / 100;

export function nodeFte(node: Pick<AdjacencyNode, 'iRnD' | 'fRnD'>, config: FteConfig): number {
  const effectiveCost = config.avgFteCost * config.fteEfficiency;
  if (effectiveCost === 0) return 0;
  return round2(((node.iRnD ?? 0) + (node.fRnD ?? 0)) / effectiveCost);
}

/** IWT / IWT South split of a node's FTE: IWT = max(0, total − IWT South). */
export function nodeFteSplit(
  node: Pick<AdjacencyNode, 'iRnD' | 'fRnD'>,
  config: FteConfig,
  iwtSouthFte: number,
): { iwt: number; iwtSouth: number; total: number } {
  const total = nodeFte(node, config);
  return { iwt: round2(Math.max(0, total - iwtSouthFte)), iwtSouth: iwtSouthFte, total };
}
